import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClinicRetentionSummary, OwnerDashboard, OwnerPvpRetentionDashboard } from '@/application/metrics';
import {
  getMetricsSummary,
  getOwnerDashboardPair,
  getOwnerPvpRetentionDashboard,
  getSedes,
} from '@/infrastructure/http/metricsApi';
import { metricsKeys } from '@/infrastructure/query/metricsKeys';
import { DomainPageHeader } from '@/presentation/components/domain/DomainPageHeader';
import { DomainScopeBar } from '@/presentation/components/domain/DomainScopeBar';
import {
  ClinicsBranchDetail,
  StatusBadge,
  type BranchOpsRow,
  type RetentionStatus,
} from '@/presentation/components/domain/ClinicsBranchDetail';
import { formatInteger, formatPercent } from '@/presentation/components/metrics/metricFormatters';
import { useGlobalPeriod } from '@/presentation/hooks/useGlobalPeriod';
import { formatMoneyCompact, formatSigned, relativeChangePct } from '@/presentation/utils/metricDeltas';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { useClinicNames } from '@/presentation/hooks/useClinicNames';
import { AccessDenied } from '@/presentation/components/common/AccessDenied';
import { PanelPageState } from '@/presentation/components/common/PanelPageState';
import { firstVisibleHousePath } from '@/application/navigation/menuHouses';
import { COMPARISON_REPORT_PATH } from '@/application/reports/reportsCatalog';
import { actionClass } from '@/presentation/components/common/actionButtons';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import { ResponsiveDataList } from '@/presentation/components/common/ResponsiveDataList';

type SortKey = 'revenue' | 'pvp' | 'patients' | 'absenteeism' | 'name';

interface ClinicOpsRow {
  tenantId: string;
  name: string;
  revenue: number;
  revenueDelta: number | null;
  pvp: number;
  pvpDelta: number | null;
  patients: number;
  absenteeism: number;
  status: RetentionStatus;
}

function statusRank(status: RetentionStatus): number {
  if (status === 'critical') return 0;
  if (status === 'alert') return 1;
  if (status === 'observation') return 2;
  return 3;
}

/** Comparativa de clínicas del scope; expand = mismo desglose operativo por sucursal. */
export function ClinicsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  const { hasPermission } = usePermissions();
  const clinicNames = useClinicNames();
  const { period, year, month, periodQuery, previousPeriodQuery, label: periodLabel } = useGlobalPeriod();
  const locale = i18n.language;
  const canView = hasPermission('dashboard.view');
  const canCompare = hasPermission('reports.compare');

  const [current, setCurrent] = useState<OwnerDashboard | null>(null);
  const [previous, setPrevious] = useState<OwnerDashboard | null>(null);
  const [retention, setRetention] = useState<OwnerPvpRetentionDashboard | null>(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('revenue');
  const [sortDesc, setSortDesc] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [branchCache, setBranchCache] = useState<Record<string, BranchOpsRow[]>>({});
  const [branchLoading, setBranchLoading] = useState<Record<string, boolean>>({});
  const [branchError, setBranchError] = useState<Record<string, string | null>>({});

  const tenantIds = useMemo(
    () => user?.scope.allowedTenantIds ?? [],
    [user?.scope.allowedTenantIds]
  );
  const previousPeriod = previousPeriodQuery;
  const pairRange =
    periodQuery.fromMonth !== undefined && periodQuery.toMonth !== undefined
      ? { fromMonth: periodQuery.fromMonth, toMonth: periodQuery.toMonth }
      : undefined;
  const queryClient = useQueryClient();
  const enabled = Boolean(token && tenantIds.length > 0 && canView);

  useEffect(() => {
    if (!canView) return;
    if (searchParams.get('view') === 'compare' && canCompare && tenantIds.length >= 2) {
      navigate(COMPARISON_REPORT_PATH, { replace: true });
      return;
    }
    if (tenantIds.length === 1) {
      navigate(`/clinics/${tenantIds[0]}?period=${period}`, { replace: true });
    }
  }, [canView, canCompare, tenantIds, period, navigate, searchParams]);

  // Al cambiar período, invalidar desgloses
  useEffect(() => {
    setExpanded(new Set());
    setBranchCache({});
    setBranchLoading({});
    setBranchError({});
  }, [year, month]);

  const pairQuery = useQuery({
    queryKey: metricsKeys.ownerDashboardPair(year, month, pairRange),
    queryFn: () => getOwnerDashboardPair(token!, periodQuery),
    enabled,
  });

  const retentionQuery = useQuery({
    queryKey: metricsKeys.ownerPvpRetention(year, month, 6),
    queryFn: () => getOwnerPvpRetentionDashboard(token!, { year, month, months: 6 }),
    enabled,
  });

  useEffect(() => {
    if (pairQuery.data) {
      setCurrent(pairQuery.data.current);
      setPrevious(pairQuery.data.previous);
    } else if (!pairQuery.isLoading) {
      setCurrent(null);
      setPrevious(null);
    }
  }, [pairQuery.data, pairQuery.isLoading]);

  useEffect(() => {
    if (retentionQuery.data) {
      setRetention(retentionQuery.data);
    } else if (!retentionQuery.isLoading) {
      setRetention(null);
    }
  }, [retentionQuery.data, retentionQuery.isLoading]);

  const isLoading = pairQuery.isLoading || retentionQuery.isLoading;
  const error =
    (pairQuery.error instanceof Error && pairQuery.error.message) ||
    (retentionQuery.error instanceof Error && retentionQuery.error.message) ||
    null;

  const retentionByTenant = useMemo(() => {
    const map = new Map<string, ClinicRetentionSummary>();
    retention?.clinics.forEach((c) => map.set(c.tenantId, c));
    return map;
  }, [retention]);

  const loadBranches = useCallback(
    async (tenantId: string) => {
      if (!token || branchCache[tenantId] || branchLoading[tenantId]) return;

      setBranchLoading((prev) => ({ ...prev, [tenantId]: true }));
      setBranchError((prev) => ({ ...prev, [tenantId]: null }));

      try {
        const sedes = await queryClient.fetchQuery({
          queryKey: metricsKeys.sedes(tenantId),
          queryFn: () => getSedes(token, tenantId),
        });
        const retClinic = retentionByTenant.get(tenantId);
        const retById = new Map((retClinic?.branches ?? []).map((b) => [b.consultorioId, b]));

        const rows = await Promise.all(
          sedes.map(async (sede) => {
            const [curSettled, prevSettled] = await Promise.allSettled([
              queryClient.fetchQuery({
                queryKey: metricsKeys.summary(tenantId, year, month, sede.id, pairRange),
                queryFn: () =>
                  getMetricsSummary(token, {
                    tenantId,
                    consultorioId: sede.id,
                    ...periodQuery,
                  }),
              }),
              queryClient.fetchQuery({
                queryKey: metricsKeys.summary(
                  tenantId,
                  previousPeriod.year,
                  previousPeriod.month ?? 0,
                  sede.id,
                  previousPeriod.fromMonth !== undefined && previousPeriod.toMonth !== undefined
                    ? { fromMonth: previousPeriod.fromMonth, toMonth: previousPeriod.toMonth }
                    : undefined
                ),
                queryFn: () =>
                  getMetricsSummary(token, {
                    tenantId,
                    consultorioId: sede.id,
                    ...previousPeriod,
                  }),
              }),
            ]);

            const cur = curSettled.status === 'fulfilled' ? curSettled.value : null;
            const prev = prevSettled.status === 'fulfilled' ? prevSettled.value : null;
            const ret = retById.get(sede.id);
            const revenue = cur?.caja.ingresos ?? 0;
            const prevRevenue = prev?.caja.ingresos ?? 0;

            return {
              consultorioId: sede.id,
              name: sede.nombre,
              revenue,
              revenueDelta: prev ? relativeChangePct(revenue, prevRevenue) : null,
              pvp: ret?.branchPvp ?? null,
              pvpDelta: ret?.branchPvpVariacionAbs ?? null,
              patients: cur?.pacientes.atendidos ?? 0,
              absenteeism: cur?.turnos.rates.absenteeism ?? 0,
              status: ret?.status ?? 'normal',
            } satisfies BranchOpsRow;
          })
        );

        rows.sort(
          (a, b) =>
            statusRank(a.status) - statusRank(b.status) ||
            b.revenue - a.revenue ||
            a.name.localeCompare(b.name)
        );

        setBranchCache((prev) => ({ ...prev, [tenantId]: rows }));
      } catch (err) {
        setBranchError((prev) => ({
          ...prev,
          [tenantId]: err instanceof Error ? err.message : t('metrics.states.error.message'),
        }));
      } finally {
        setBranchLoading((prev) => ({ ...prev, [tenantId]: false }));
      }
    },
    [token, branchCache, branchLoading, retentionByTenant, year, month, periodQuery, pairRange, previousPeriod, t, queryClient]
  );

  const rows = useMemo(() => {
    if (!current || !retention) return [];
    const prevMap = new Map(previous?.clinics.map((c) => [c.tenantId, c.summary]) ?? []);
    const opsMap = new Map(current.clinics.map((c) => [c.tenantId, c.summary]));

    let list = retention.clinics
      .filter((clinic) => tenantIds.includes(clinic.tenantId))
      .map((clinic) => {
      const ops = opsMap.get(clinic.tenantId);
      const prev = prevMap.get(clinic.tenantId);
      const revenue = ops?.caja.ingresos ?? 0;
      const prevRevenue = prev?.caja.ingresos ?? 0;
      return {
        tenantId: clinic.tenantId,
        name: clinicNames[clinic.tenantId] ?? clinic.tenantId,
        revenue,
        revenueDelta: relativeChangePct(revenue, prevRevenue),
        pvp: clinic.clinicPvp,
        pvpDelta: clinic.clinicPvpVariacionAbs,
        patients: ops?.pacientes.atendidos ?? 0,
        absenteeism: ops?.turnos.rates.absenteeism ?? 0,
        status: clinic.status,
      };
    });

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((row) => row.name.toLowerCase().includes(q));
    }

    list.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortKey === 'revenue') cmp = a.revenue - b.revenue;
      else if (sortKey === 'pvp') cmp = a.pvp - b.pvp;
      else if (sortKey === 'patients') cmp = a.patients - b.patients;
      else cmp = a.absenteeism - b.absenteeism;
      if (sortDesc) cmp = -cmp;
      if (cmp === 0) cmp = statusRank(a.status) - statusRank(b.status);
      return cmp;
    });

    return list;
  }, [current, previous, retention, clinicNames, search, sortKey, sortDesc, tenantIds]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDesc((v) => !v);
    } else {
      setSortKey(key);
      setSortDesc(key !== 'name');
    }
  };

  const toggleExpand = (tenantId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) {
        next.delete(tenantId);
      } else {
        next.add(tenantId);
        void loadBranches(tenantId);
      }
      return next;
    });
  };

  const openClinic = (tenantId: string) => {
    navigate(`/clinics/${tenantId}?period=${period}`);
  };

  const colCount = 7;
  const sortMark = (key: SortKey) => (sortKey === key ? (sortDesc ? ' ↓' : ' ↑') : '');

  const columns = [
      {
        key: 'expand',
        header: <span className="sr-only">{t('domain.clinics.branchesTitle')}</span>,
        headerClassName: 'w-8 px-1',
        cellClassName: 'px-1 text-center',
        cell: (row: ClinicOpsRow) => {
          const isOpen = expanded.has(row.tenantId);
          return (
            <button
              type="button"
              onClick={() => toggleExpand(row.tenantId)}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-slate-100 hover:text-teal-700 dark:hover:bg-white/10 dark:hover:text-teal-300"
              aria-expanded={isOpen}
              aria-label={
                isOpen
                  ? t('domain.clinics.collapseAria', { name: row.name })
                  : t('domain.clinics.expandAria', { name: row.name })
              }
            >
              <span className="text-xs font-bold" aria-hidden>
                {isOpen ? '▼' : '▶'}
              </span>
            </button>
          );
        },
      },
      {
        key: 'name',
        sortable: true,
        sortLabel: t('dashboard.command.table.clinic'),
        header: (
          <button type="button" className="hover:text-teal-700 dark:hover:text-teal-300" onClick={() => toggleSort('name')}>
            {t('dashboard.command.table.clinic')}
            {sortMark('name')}
          </button>
        ),
        cell: (row: ClinicOpsRow) => (
          <button
            type="button"
            onClick={() => openClinic(row.tenantId)}
            className="text-left font-semibold text-gray-900 hover:text-teal-700 dark:text-white dark:hover:text-teal-300"
          >
            {row.name}
          </button>
        ),
      },
      {
        key: 'revenue',
        align: 'left' as const,
        sortable: true,
        sortLabel: t('dashboard.command.table.revenue'),
        header: (
          <button
            type="button"
            className="hover:text-teal-700 dark:hover:text-teal-300"
            onClick={() => toggleSort('revenue')}
          >
            {t('dashboard.command.table.revenue')}
            {sortMark('revenue')}
          </button>
        ),
        cell: (row: ClinicOpsRow) => (
          <span className="tabular-nums">
            {formatMoneyCompact(locale, row.revenue)}
            {row.revenueDelta !== null ? (
              <span className="ml-1 text-[11px] text-gray-500">
                {formatSigned(locale, row.revenueDelta)}%
              </span>
            ) : null}
          </span>
        ),
      },
      {
        key: 'pvp',
        sortable: true,
        sortLabel: t('dashboard.command.table.pvp'),
        header: (
          <button type="button" className="hover:text-teal-700 dark:hover:text-teal-300" onClick={() => toggleSort('pvp')}>
            {t('dashboard.command.table.pvp')}
            {sortMark('pvp')}
          </button>
        ),
        cell: (row: ClinicOpsRow) => (
          <span className="tabular-nums">
            {row.pvp.toLocaleString(locale, { maximumFractionDigits: 1 })}
            {row.pvpDelta !== null ? (
              <span className="ml-1 text-[11px] text-gray-500">{formatSigned(locale, row.pvpDelta)}</span>
            ) : null}
          </span>
        ),
      },
      {
        key: 'patients',
        sortable: true,
        sortLabel: t('dashboard.command.table.patients'),
        header: (
          <button
            type="button"
            className="hover:text-teal-700 dark:hover:text-teal-300"
            onClick={() => toggleSort('patients')}
          >
            {t('dashboard.command.table.patients')}
            {sortMark('patients')}
          </button>
        ),
        cell: (row: ClinicOpsRow) => formatInteger(row.patients, locale),
        cellClassName: 'tabular-nums',
      },
      {
        key: 'absenteeism',
        sortable: true,
        sortLabel: t('dashboard.command.table.absenteeism'),
        header: (
          <button
            type="button"
            className="hover:text-teal-700 dark:hover:text-teal-300"
            onClick={() => toggleSort('absenteeism')}
          >
            {t('dashboard.command.table.absenteeism')}
            {sortMark('absenteeism')}
          </button>
        ),
        cell: (row: ClinicOpsRow) => formatPercent(row.absenteeism, locale),
        cellClassName: 'tabular-nums',
      },
      {
        key: 'status',
        header: t('dashboard.command.table.status'),
        cell: (row: ClinicOpsRow) => <StatusBadge status={row.status} />,
      },
    ];

  if (!canView) {
    return <AccessDenied domain="clinics" homeTo={firstVisibleHousePath(hasPermission)} />;
  }

  return (
    <div className="panel-page panel-safe-bottom space-y-5">
      <DomainPageHeader
        question={t('domain.clinics.question')}
        title={t('domain.clinics.title')}
        subtitle={t('domain.clinics.subtitle', {
          period: periodLabel,
          count: tenantIds.length,
        })}
      >
        {canCompare && tenantIds.length >= 2 ? (
          <Link to={COMPARISON_REPORT_PATH} data-testid="compare-clinics-cta" className={actionClass('primary', 'page')}>
            {t('domain.clinics.compareCta')}
          </Link>
        ) : null}
      </DomainPageHeader>

      <DomainScopeBar
        actions={
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('domain.clinics.search')}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900"
          />
        }
      />

      {error && (
        <PanelPageState
          variant="error"
          layout="inline"
          onRetry={() => {
            void pairQuery.refetch();
            void retentionQuery.refetch();
          }}
        />
      )}

      <ResponsiveDataList
        rows={rows}
        keyField={(row) => row.tenantId}
        columns={columns}
        loading={isLoading}
        loadingSlot={<LoadingBlock minHeightClassName="min-h-[12rem]" />}
        emptyMessage={t('domain.clinics.empty')}
        ariaLabel={t('domain.clinics.title')}
        sort={{ key: sortKey, direction: sortDesc ? 'desc' : 'asc' }}
        onSortChange={({ key, direction }) => {
          setSortKey(key as SortKey);
          setSortDesc(direction === 'desc');
        }}
        renderCard={(row) => (
            <div data-testid={'clinics-mobile-card-' + row.tenantId}>
              <div className="flex items-start justify-between gap-2">
                <button
                  type="button"
                  onClick={() => openClinic(row.tenantId)}
                  className="min-w-0 text-left font-semibold text-gray-900 dark:text-white"
                >
                  {row.name}
                </button>
                <StatusBadge status={row.status} />
              </div>
              <button
                type="button"
                onClick={() => openClinic(row.tenantId)}
                className="-mx-3 mt-2 box-border w-[calc(100%+1.5rem)] px-3 text-left"
              >
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                <div className="min-w-0">
                  <dt className="truncate text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('dashboard.command.table.revenue')}
                  </dt>
                  <dd className="break-words text-sm font-semibold tabular-nums text-gray-800 dark:text-gray-100">
                    {formatMoneyCompact(locale, row.revenue)}
                    {row.revenueDelta !== null ? (
                      <span className="ml-1 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                        {formatSigned(locale, row.revenueDelta)}%
                      </span>
                    ) : (
                      <span className="ml-1 text-[11px] font-medium text-gray-400">
                        {t('dashboard.command.noDelta')}
                      </span>
                    )}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="truncate text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t('dashboard.command.table.pvp')}
                  </dt>
                  <dd className="break-words text-sm font-semibold tabular-nums text-gray-800 dark:text-gray-100">
                    {row.pvp.toLocaleString(locale, { maximumFractionDigits: 1 })}
                    {row.pvpDelta !== null ? (
                      <span className="ml-1 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                        {formatSigned(locale, row.pvpDelta)}
                      </span>
                    ) : null}
                  </dd>
                </div>
              </dl>
              </button>
            </div>
        )}
        renderAfterRow={(row) =>
          expanded.has(row.tenantId) ? (
            <ClinicsBranchDetail
              clinicRevenue={row.revenue}
              clinicPatients={row.patients}
              locale={locale}
              branches={branchCache[row.tenantId]}
              loading={Boolean(branchLoading[row.tenantId])}
              error={branchError[row.tenantId] ?? null}
              colCount={colCount}
              onOpenClinic={() => openClinic(row.tenantId)}
            />
          ) : null
        }
      />
    </div>
  );
}
